/**
 * Responsibility: build the land and water functions for a biome whose water sits in scattered basins.
 *
 * ## Why this is shared
 *
 * Three biomes hold lakes and differ only in how their ground is shaped: bumpy glacial lakeland, gentle open lowland, and
 * wooded lowland. The basin arithmetic is identical in all three, so it lives here once and each biome passes its own
 * tuning block. The alternative was the same eighty lines three times, drifting apart at the first fix.
 *
 * ## Blobs are the easy case, and for once that is what is wanted
 *
 * Every water feature before lakes fought the fact that thresholded noise gives **blobs rather than channels** — it is why
 * a river had to be carved along a field's zero-crossing contours. A lake wants exactly that blob: threshold a
 * low-frequency field, carve a hollow where it is over the line, fill it.
 *
 * ## The floor ignores the ground above it, and that is the load-bearing detail
 *
 * A basin floor is set by the regional base alone. A lake does not sit higher because the ground beside it happens to
 * bulge — and the visible consequence is that water comes out broader where the land is low and pinched where a rise
 * crowds it, with nothing in the code saying so. The same property makes a river read as a valley.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from './noise.js';
import { carveHollow } from './hollow.js';
import { WATER } from '../config.js';

/** Tuning fields a lake biome has to supply. Checked up front, because a missing one reads as flat terrain. */
const REQUIRED = [
  'BASE_HEIGHT',
  'BASE_RELIEF',
  'BASE_WAVELENGTH',
  'GROUND_RELIEF',
  'GROUND_WAVELENGTH',
  'GROUND_OCTAVES',
  'GROUND_PERSISTENCE',
  'LACUNARITY',
  'BASIN_WAVELENGTH',
  'BASIN_OCTAVES',
  'BASIN_THRESHOLD',
  'BASIN_RAMP',
  'DEPTH',
  'SHORE_SHARPNESS',
  'WATER_DROP',
];

/**
 * Build the land and water functions for one lake biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Labels the noise fields, so sibling biomes do not share a layout.
 * @param {object} tuning - The biome's config block.
 * @returns {{elevation: Function, ownDepth: Function, insideBasin: Function, nearBasin: Function, maxElevation: number}}
 */
export function createLakeTerrain(seed, biomeId, tuning) {
  for (const key of REQUIRED) {
    if (!Number.isFinite(tuning?.[key])) {
      throw new Error(`createLakeTerrain: ${biomeId} is missing tuning value ${key}`);
    }
  }
  if (!(tuning.BASE_HEIGHT > tuning.DEPTH)) {
    // Otherwise a basin floor wants to go below zero and gets clamped, which shows as lakes all at one height.
    throw new Error(`createLakeTerrain: ${biomeId} BASE_HEIGHT must exceed DEPTH`);
  }

  /*
    Long wavelength. This is the regional ground level and the water follows it, so the slower it drifts the less two
    nearby lakes disagree about how high water sits.
  */
  const baseField = createFbm(seed, `base:${biomeId}`, {
    octaves: 2,
    baseFrequency: 1 / tuning.BASE_WAVELENGTH,
    persistence: 0.45,
    lacunarity: tuning.LACUNARITY,
  });

  /** The shape of the land between the water. The only thing that differs between the three lake biomes. */
  const groundField = createFbm(seed, `ground:${biomeId}`, {
    octaves: tuning.GROUND_OCTAVES,
    baseFrequency: 1 / tuning.GROUND_WAVELENGTH,
    persistence: tuning.GROUND_PERSISTENCE,
    lacunarity: tuning.LACUNARITY,
  });

  /*
    Used as a plain magnitude, unlike a river's channel field. It is the *peaks* of this one that become lakes, which is
    what makes them rounded and unconnected where a river is linear and branching.
  */
  const basinField = createFbm(seed, `basins:${biomeId}`, {
    octaves: tuning.BASIN_OCTAVES,
    baseFrequency: 1 / tuning.BASIN_WAVELENGTH,
    persistence: 0.5,
    lacunarity: tuning.LACUNARITY,
  });

  const baseAt = (x, z) => tuning.BASE_HEIGHT + toUnitRange(baseField(x, z)) * tuning.BASE_RELIEF;
  const landAt = (x, z) => baseAt(x, z) + toUnitRange(groundField(x, z)) * tuning.GROUND_RELIEF;

  /** How strongly a basin claims a point, before it is turned into a rim distance. */
  const strengthAt = (x, z) => (toUnitRange(basinField(x, z)) - tuning.BASIN_THRESHOLD) / tuning.BASIN_RAMP;

  /**
   * How far out toward a basin's rim a coordinate sits: 0 at the deepest point, 1 at the rim, above 1 outside.
   *
   * Expressed this way rather than as a strength so it plugs straight into the hollow profile a river channel uses.
   */
  const fromRim = (x, z) => 1 - Math.min(1, Math.max(0, strengthAt(x, z)));

  const carve = (x, z) =>
    carveHollow(landAt(x, z), baseAt(x, z) - tuning.DEPTH, fromRim(x, z), tuning.SHORE_SHARPNESS);

  /**
   * Height the water sits at, from the base field alone.
   *
   * Named and hoisted rather than inlined because both the depth and the drawn surface have to use the *same* level, and
   * the whole flatness of the water rests on that level containing no terrain detail.
   */
  const waterLevelAt = (x, z) => baseAt(x, z) - tuning.WATER_DROP;

  return {
    /** Highest elevation this biome can produce. Basins only cut down, so they cannot raise it. */
    maxElevation: tuning.BASE_HEIGHT + tuning.BASE_RELIEF + tuning.GROUND_RELIEF,

    /**
     * Ground height, in world units and never negative.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    elevation(x, z) {
      return Math.max(0, carve(x, z));
    },

    /**
     * Depth of the water at a coordinate, before biome weight is applied.
     *
     * **Signed, and deliberately not clamped.** The waterline is interpolated from this along a grid cell edge, and a value
     * clamped at zero puts the crossing at the dry end so nothing gets clipped. That mistake has been made twice in this
     * project's history; the sign is the fix both times.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number} Positive under water, negative on dry land.
     */
    ownDepth(x, z) {
      return waterLevelAt(x, z) - carve(x, z);
    },

    /**
     * Height the water surface sits at.
     *
     * **A function of the base field only, with no ground detail in it, and that is the entire point.** The base runs at
     * `BASE_WAVELENGTH` — thousands of units — so across one lake this is very nearly constant, which is what a lake
     * surface has to be. The bumpy `landAt` field is deliberately absent: drawing the surface from anything containing it
     * is what made the water look lumpy, and `carve` contains it, so the depth cannot be used to derive a height.
     *
     * Different lakes still sit at different altitudes, which is correct — they are different lakes.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    waterLevel: waterLevelAt,

    /** Cheap rejection for the surface pass: is there a basin here at all. */
    insideBasin(x, z) {
      return fromRim(x, z) < 1;
    },

    /**
     * Cheap rejection for the shore test, deliberately looser.
     *
     * A basin's water stops well inside its rim, so a wide object centred *outside* the basin can still overhang the lake —
     * and those are exactly the cases the shore test exists to catch.
     */
    nearBasin(x, z) {
      return strengthAt(x, z) > -WATER.CLEARANCE_GATE;
    },
  };
}
